import React, { ReactNode, useMemo } from 'react';
import { gql } from '@common/hooks';
import { Table } from 'antd';
import { Colors } from '@common/tokens';
import { Spacing } from 'works/legacy/lib/style-helper';
import { ExpirationDetails } from './_expiration-details';
import { InfoTooltip } from '@common/components';
import {
  WorksListItemExpirationType,
  worksListItemShortToLongTypeMap as longTypes,
  worksListItemExpirationTitles,
} from '@onboarding/services';
import {
  DetailItemType,
  WorksListItemType,
  WorksListType,
} from '@onboarding/graphql';
import { ComplianceItemStatusIcon } from '@onboarding/components/compliance-item-status-icon/compliance-item-status-icon';
import { DetailItemAttachments } from '@onboarding/graphql/fragments';

type Props = {
  worksList: WorksListType;
  actionColumns: {
    title: string;
    key: string;
    render: (worksListItem: WorksListItemType) => ReactNode;
  }[];
};

export function ComplianceItemTables({ worksList, actionColumns = [] }: Props) {
  const worksListItems = worksList.worksListItems;

  const backgroundIdItems = useMemo(
    () =>
      worksListItems.filter((item) => item.type === longTypes.identification),
    [worksListItems]
  );

  const immigrationItems = useMemo(
    () => worksListItems.filter((item) => item.type === longTypes.immigration),
    [worksListItems]
  );

  const licenseItems = useMemo(
    () => worksListItems.filter((item) => item.type === longTypes.license),
    [worksListItems]
  );

  const certificationItems = useMemo(
    () =>
      worksListItems.filter((item) => item.type === longTypes.certification),
    [worksListItems]
  );

  const assessmentsItems = useMemo(
    () => worksListItems.filter((item) => item.type === longTypes.assessment),
    [worksListItems]
  );

  const healthDocumentItems = useMemo(
    () => worksListItems.filter((item) => item.type === longTypes.health_doc),
    [worksListItems]
  );

  const otherDocumentItems = useMemo(
    () => worksListItems.filter((item) => item.type === longTypes.other_doc),
    [worksListItems]
  );

  const getIconState = (worksListItem: WorksListItemType) => {
    if (!worksListItem.active) {
      return 'inactive';
    }

    if (!worksListItem.state) {
      return 'new';
    }

    return worksListItem.state;
  };

  const commonDataColumns = [
    {
      title: '',
      dataIndex: 'id',
      onHeaderCell: () => {
        return { style: { width: '40px' } };
      },
      render: (listItemId: string, worksListItem: WorksListItemType) => {
        return (
          <div className="list-status">
            <ComplianceItemStatusIcon
              status={getIconState(worksListItem)}
              size="lg"
            />
            <style jsx>{`
              .list-status > :global(.fa-check-circle) {
                font-size: 20px;
              }

              .list-status {
                max-height: 25px;
              }
            `}</style>
          </div>
        );
      },
    },
    {
      title: '',
      dataIndex: 'name',
      className: 'date-header',
      onHeaderCell: () => {
        return { style: { width: '30%' } };
      },
      render: (name: string, worksListItem: WorksListItemType) => {
        return (
          <>
            <div className="works-item-name">{name}</div>
            {worksListItem.state === 'rejected' && (
              <>
                {worksListItem.rejectionReasonDescription && (
                  <div className="works-item-rejection">
                    {worksListItem.rejectionReasonDescription}
                  </div>
                )}
                {worksListItem.rejectionDetails && (
                  <div>{`"${worksListItem.rejectionDetails}"`}</div>
                )}
              </>
            )}
            <div>
              <span className="secondary-paragraph">
                {worksListItem.description}
              </span>
            </div>
            <style jsx>{`
              .works-item-name {
                font-weight: 800;
              }
              .works-item-rejection {
                color: ${Colors.coral};
              }
            `}</style>
          </>
        );
      },
    },
  ];

  const expirationColumns = [
    {
      title: 'Expiration Type',
      dataIndex: 'expirationType',
      className: 'date-header',
      render: (expirationType: WorksListItemExpirationType) => {
        return <span>{worksListItemExpirationTitles[expirationType]}</span>;
      },
    },
    {
      title: 'Expiration',
      dataIndex: 'detailItem',
      className: 'date-header',
      render: (
        detailItem: DetailItemType,
        worksListItem: WorksListItemType
      ) => {
        return (
          detailItem && (
            <ExpirationDetails
              expiresOn={worksListItem.expiresOn}
              isExpired={!worksListItem.compliant}
              isExpiringSoon={
                !worksListItem.compliantForAssignment &&
                worksListItem.isExpiringSoon
              }
            />
          )
        );
      },
    },
    {
      title: 'Revised Expiration',
      dataIndex: 'detailItem',
      className: 'date-header',
      render: (
        detailItem: DetailItemType,
        worksListItem: WorksListItemType
      ) => {
        return (
          detailItem && (
            <ExpirationDetails
              expiresOn={worksListItem.revisedExpiresOn}
              isExpired={worksListItem.revisedExpired}
              isExpiringSoon={
                !worksListItem.revisedCompliantForAssignment &&
                worksListItem.isRevisedExpiringSoon
              }
            />
          )
        );
      },
    },
  ];

  return (
    <div className="onboarding-item-tables sentry-mask">
      {backgroundIdItems.length > 0 && (
        <section>
          <h4 className="ant-typography section-title">
            Background and Identification
          </h4>
          <Table
            pagination={false}
            className="approval-table"
            rowKey="id"
            columns={[
              ...commonDataColumns,
              ...[
                {
                  title: 'Type',
                  dataIndex: 'documentTemplate',
                  key: 'type',
                  render: (documentTemplate: { name: string }) => {
                    return <span>{documentTemplate?.name}</span>;
                  },
                },
                {
                  title: 'Subtype',
                  dataIndex: 'documentTemplateSubtype',
                  key: 'subtype',
                  render: (documentTemplateSubtype: string) => {
                    return <span>{documentTemplateSubtype}</span>;
                  },
                },
              ],
              ...expirationColumns,
              ...actionColumns,
            ]}
            dataSource={backgroundIdItems}
          />
        </section>
      )}

      {immigrationItems.length > 0 && (
        <section>
          <h4 className="ant-typography section-title">Immigration</h4>
          <Table
            pagination={false}
            className="approval-table"
            rowKey="id"
            columns={[
              ...commonDataColumns,
              ...[
                {
                  title: 'Type',
                  dataIndex: 'documentTemplate',
                  key: 'type',
                  render: (documentTemplate: { name: string }) => {
                    return <span>{documentTemplate?.name}</span>;
                  },
                },
              ],
              ...expirationColumns,
              ...actionColumns,
            ]}
            dataSource={immigrationItems}
          />
        </section>
      )}

      {licenseItems.length > 0 && (
        <section>
          <h4 className="ant-typography section-title">Licenses</h4>
          <Table
            pagination={false}
            rowKey="id"
            columns={[
              ...commonDataColumns,
              ...[
                {
                  title: 'License #',
                  dataIndex: 'detailItem',
                  render: (detailItem: DetailItemType) => {
                    return <span>{detailItem?.number}</span>;
                  },
                },
                {
                  title: 'State',
                  dataIndex: 'detailItem',
                  render: (detailItem: DetailItemType) => {
                    return <span>{detailItem?.stateCode}</span>;
                  },
                },
                {
                  title: 'Multistate / Compact',
                  dataIndex: 'detailItem',
                  render: (detailItem: DetailItemType) => {
                    return (
                      <span>
                        {detailItem && (detailItem.compact ? 'Yes' : 'No')}
                      </span>
                    );
                  },
                },
              ],
              ...expirationColumns,
              ...actionColumns,
            ]}
            dataSource={licenseItems}
          />
        </section>
      )}

      {certificationItems.length > 0 && (
        <section>
          <h4 className="ant-typography section-title">Certifications</h4>
          <Table
            pagination={false}
            rowKey="id"
            columns={[
              ...commonDataColumns,
              ...[
                {
                  title: 'Issuing Bodies',
                  dataIndex: 'certification',
                  render: (
                    certification: { issuingBodies: string[] } | undefined
                  ) => {
                    return (
                      <span>{certification?.issuingBodies.join(', ')}</span>
                    );
                  },
                },
              ],
              ...expirationColumns,
              ...actionColumns,
            ]}
            dataSource={certificationItems}
          />
        </section>
      )}

      {assessmentsItems.length > 0 && (
        <section>
          <h4 className="ant-typography section-title">Assessments</h4>
          <Table
            pagination={false}
            rowKey="id"
            columns={[
              ...commonDataColumns,
              ...[
                {
                  title: 'Type',
                  dataIndex: 'assessment',
                  render: (assessment: { category: string } | undefined) => {
                    return <span>{assessment?.category}</span>;
                  },
                },
                {
                  title: 'Accepted Issuer(s)',
                  dataIndex: 'assessmentIssuers',
                  render: (
                    assessmentIssuers: { name: string }[] | undefined
                  ) => {
                    if (!assessmentIssuers || assessmentIssuers.length === 0) {
                      return <></>;
                    }

                    if (assessmentIssuers.length > 1) {
                      return (
                        <>
                          Mutiple
                          <InfoTooltip
                            size="1x"
                            leftOffset={4}
                            title="This assessment item accepts a list of different issuers."
                          />
                        </>
                      );
                    } else {
                      return <span>{assessmentIssuers[0].name}</span>;
                    }
                  },
                },
                {
                  title: 'Selected Issuer',
                  dataIndex: 'issuedAssessment',
                  render: (issuedAssessment: { issuer?: { name: string } }) => {
                    return <span>{issuedAssessment?.issuer?.name}</span>;
                  },
                },
              ],
              ...expirationColumns,
              ...actionColumns,
            ]}
            dataSource={assessmentsItems}
          />
        </section>
      )}

      {healthDocumentItems.length > 0 && (
        <section>
          <h4 className="ant-typography section-title">Health Documents</h4>
          <Table
            pagination={false}
            rowKey="id"
            columns={[
              ...commonDataColumns,
              ...[
                {
                  title: 'Type',
                  dataIndex: 'healthDocSubtype',
                  render: (type: string) => {
                    return <span>{type}</span>;
                  },
                },
                {
                  title: 'Date Completed',
                  dataIndex: 'detailItem',
                  className: 'date-header',
                  render: (detailItem: DetailItemType) => {
                    return <span>{detailItem && detailItem.completedOn}</span>;
                  },
                },
              ],
              ...expirationColumns,
              ...actionColumns,
            ]}
            dataSource={healthDocumentItems}
          />
        </section>
      )}

      {otherDocumentItems.length > 0 && (
        <section>
          <h4 className="ant-typography section-title">Other</h4>
          <Table
            pagination={false}
            rowKey="id"
            columns={[
              ...commonDataColumns,
              ...[
                {
                  title: '',
                  dataIndex: 'id',
                  render: () => {
                    return '';
                  },
                },
              ],
              ...expirationColumns,
              ...actionColumns,
            ]}
            dataSource={otherDocumentItems}
          />
        </section>
      )}

      <style jsx>{`
        .onboarding-item-tables h4.section-title {
          margin-bottom: 0;
          padding-bottom: 0;
          margin-top: ${Spacing.baseSpacing * 2}px;
        }
        .onboarding-item-tables :global(.date-header) {
          width: 140px;
        }
      `}</style>
    </div>
  );
}

ComplianceItemTables.fragments = {
  worksList: gql`
    fragment OnboardingItemListWorksListInfo on WorksList {
      id
      canUpdate {
        value
      }
      canApproveReject {
        value
      }
      worksListTemplate {
        id
        complianceDeadlineDetails
      }
      worksListItems {
        id
        active
        name
        description
        state
        type
        rejectionReasonDescription
        rejectionDetails
        compliant
        compliantForAssignment
        revisedExpired
        revisedCompliantForAssignment

        stateApprovedBy {
          id
          name
        }
        stateRejectedBy {
          id
          name
        }
        stateApprovedAt
        stateRejectedAt
        expiresOn
        revisedExpiresOn
        isExpiringSoon
        isRevisedExpiringSoon
        expirationType

        detailItem {
          ... on License {
            id
            stateCode
            compact
            expiresOn
            number
            detailItemAttachments {
              ...DetailItemAttachments
            }
          }
          ... on NurseAssessment {
            id
            expiresOn
            completedOn
            detailItemAttachments {
              ...DetailItemAttachments
            }
          }
          ... on NurseHealthDoc {
            id
            completedOn
            expiresOn
            healthDoc {
              id
            }
            detailItemAttachments {
              ...DetailItemAttachments
            }
          }
          ... on NurseCertification {
            id
            name
            expiresOn
            issuingBody
            certificationUuid
            detailItemAttachments {
              ...DetailItemAttachments
            }
          }
          ... on Document {
            id
            completedOn
            expiresOn
            docType
            detailItemAttachments {
              ...DetailItemAttachments
            }
          }
        }

        ... on AssessmentItem {
          assessmentIssuers {
            id
            name
          }
          assessmentIssuersToBeSelected {
            id
            name
          }
          assessment {
            id
            name
            category
          }
          issuedAssessment {
            id
            assessment {
              id
              name
              category
            }
            issuer {
              id
              name
            }
          }
        }

        ... on BackgroundIdentificationItem {
          documentTemplate {
            id
            name
            key
          }
          documentTemplateSubtype
        }

        ... on ImmigrationItem {
          documentTemplate {
            id
            name
            key
          }
          documentTemplateSubtype
        }

        ... on CertificationItem {
          certification {
            id
            name
            issuingBodies
            credential
          }
        }

        ... on HealthDocumentItem {
          healthDocSubtype
          healthDoc {
            id
            name
          }
        }

        ... on LicenseItem {
          licenseType
        }
      }
    }
    ${DetailItemAttachments}
  `,
};
